import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';

const EditUserAccount = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    heightFeet: '',
    heightInches: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    instagram: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);

  useEffect(() => {
    fetchUserData();
  }, []); // Only run on mount - do NOT refresh after updates

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found. Please log in again.');
        setLoadingUser(false);
        return;
      }
      
      // Set cookie as fallback
      document.cookie = `auth_token=${token}; path=/; max-age=86400; SameSite=Lax; Secure`;
      
      // Try with Authorization header first, plus X-Auth-Token as fallback
      let response;
      try {
        response = await axios.get(`${API_BASE_URL}/auth/user`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'X-Auth-Token': token
          },
          withCredentials: true
        });
      } catch (err) {
        // If 401, try with query parameter as last resort
        if (err.response?.status === 401) {
          console.log('Authorization header failed, trying with query parameter...');
          response = await axios.get(`${API_BASE_URL}/auth/user`, {
            params: { token: token },
            headers: { 
              'X-Auth-Token': token
            },
            withCredentials: true
          });
        } else {
          throw err;
        }
      }
      
      const userData = { ...response.data };
      
      // Format dateOfBirth to YYYY-MM-DD format for date input
      // MySQL returns DATE fields as YYYY-MM-DD strings, use directly to avoid timezone issues
      if (userData.dateOfBirth) {
        const dateStr = String(userData.dateOfBirth).trim();
        
        // Check if it's already in YYYY-MM-DD format (most common case from MySQL)
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          // Already in correct format, use as is
          userData.dateOfBirth = dateStr;
        } else {
          // Try to extract YYYY-MM-DD from the string (handles timestamps, etc.)
          const dateMatch = dateStr.match(/(\d{4})-(\d{2})-(\d{2})/);
          if (dateMatch) {
            // Extract and use the date part directly
            userData.dateOfBirth = dateMatch[0];
          } else {
            // Last resort: parse manually to avoid timezone issues
            const dateParts = dateStr.split(/[-/T\s]/);
            if (dateParts.length >= 3) {
              const year = parseInt(dateParts[0], 10);
              const month = parseInt(dateParts[1], 10);
              const day = parseInt(dateParts[2], 10);
              if (!isNaN(year) && !isNaN(month) && !isNaN(day) && year > 1900 && year < 2100) {
                userData.dateOfBirth = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              } else {
                userData.dateOfBirth = '';
              }
            } else {
              userData.dateOfBirth = '';
            }
          }
        }
      }
      
      // Ensure ALL fields are properly set, even if missing from response
      const initialFormData = {
        firstName: userData.firstName ?? '',
        lastName: userData.lastName ?? '',
        email: userData.email ?? '',
        phone: userData.phone ?? '',
        dateOfBirth: userData.dateOfBirth ?? '',
        heightFeet: userData.heightFeet ?? '',
        heightInches: userData.heightInches ?? '',
        gender: userData.gender ?? '',
        address: userData.address ?? '',
        city: userData.city ?? '',
        state: userData.state ?? '',
        zipCode: userData.zipCode ?? '',
        country: userData.country ?? '',
        instagram: userData.instagram ?? ''
      };
      
      // Verify all expected fields are present
      const expectedFields = ['firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 'heightFeet', 'heightInches', 'gender', 'address', 'city', 'state', 'zipCode', 'country', 'instagram'];
      expectedFields.forEach(field => {
        if (!(field in initialFormData)) {
          console.warn(`Missing field in initialFormData: ${field}`);
          initialFormData[field] = '';
        }
      });
      
      console.log('Setting initial formData with all fields:', initialFormData);
      setFormData(initialFormData);
      setError('');
    } catch (err) {
      console.error('Failed to fetch user data:', err);
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else if (err.response?.status === 404) {
        setError('User not found. Please log in again.');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch user data. Please try again.');
      }
    } finally {
      setLoadingUser(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for dateOfBirth to ensure proper format
    if (name === 'dateOfBirth') {
      // Date input already provides YYYY-MM-DD format, but ensure it's clean
      const cleanDate = value.trim();
      console.log('Date changed:', cleanDate);
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: cleanDate || ''
      }));
    } else {
      // Use functional update to ensure we're working with latest state
      setFormData(prevFormData => {
        const updated = {
          ...prevFormData,
          [name]: value
        };
        console.log(`Field ${name} changed to: "${value}"`);
        console.log('Updated formData:', updated);
        return updated;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found. Please log in again.');
        setLoading(false);
        return;
      }
      
      // Prepare data - ensure all fields are included, convert empty strings to null for optional fields
      // For dateOfBirth, ensure it's in YYYY-MM-DD format (date input already provides this)
      let dateOfBirthValue = formData.dateOfBirth?.trim() || null;
      
      console.log('Original dateOfBirth from formData:', formData.dateOfBirth);
      
      // Validate and normalize date format if provided
      if (dateOfBirthValue) {
        // Remove any whitespace
        dateOfBirthValue = dateOfBirthValue.trim();
        
        // Check if already in correct YYYY-MM-DD format
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirthValue)) {
          // Already in correct format
          console.log('Date is in correct format:', dateOfBirthValue);
        } else {
          // If date is not in correct format, try to fix it
          const dateMatch = dateOfBirthValue.match(/(\d{4})-(\d{2})-(\d{2})/);
          if (dateMatch) {
            dateOfBirthValue = dateMatch[0];
            console.log('Date format corrected:', dateOfBirthValue);
          } else {
            console.warn('Invalid date format, setting to null:', dateOfBirthValue);
            dateOfBirthValue = null;
          }
        }
      } else {
        console.log('Date of birth is empty or null');
      }
      
      // IMPORTANT: Always include ALL fields in the update, even if empty/null
      // This ensures the backend processes all fields and returns them in the response
      const updateData = {
        firstName: formData.firstName?.trim() || '',
        lastName: formData.lastName?.trim() || '',
        email: formData.email?.trim() || '',
        phone: formData.phone?.trim() || null,
        dateOfBirth: dateOfBirthValue || null, // Always include, even if null
        heightFeet: formData.heightFeet ? parseInt(formData.heightFeet, 10) : null,
        heightInches: formData.heightInches ? parseInt(formData.heightInches, 10) : null,
        gender: formData.gender?.trim() || null,
        address: formData.address?.trim() || null,
        city: formData.city?.trim() || null,
        state: formData.state?.trim() || null,
        zipCode: formData.zipCode?.trim() || null,
        country: formData.country?.trim() || null,
        instagram: formData.instagram?.trim() || null
      };
      
      // Ensure ALL fields are present in updateData (defensive programming)
      const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 'heightFeet', 'heightInches', 'gender', 'address', 'city', 'state', 'zipCode', 'country', 'instagram'];
      requiredFields.forEach(field => {
        if (!(field in updateData)) {
          if (field === 'dateOfBirth') {
            updateData[field] = null;
          } else if (['firstName', 'lastName', 'email'].includes(field)) {
            updateData[field] = '';
          } else {
            updateData[field] = null;
          }
        }
      });
      
      console.log('All fields in updateData:', Object.keys(updateData));
      console.log('Update data values:', updateData);
      
      // Log the data being sent for debugging
      console.log('=== SUBMITTING UPDATE REQUEST ===');
      console.log('API URL:', `${API_BASE_URL}/auth/update-user`);
      console.log('Update data being sent:', JSON.stringify(updateData, null, 2));
      console.log('Token present:', !!token);
      console.log('Token length:', token ? token.length : 0);
      
      // Set cookie as fallback
      document.cookie = `auth_token=${token}; path=/; max-age=86400; SameSite=Lax; Secure`;
      
      // Try with Authorization header first, plus X-Auth-Token as fallback
      let response;
      try {
        console.log('Attempting PUT request with Authorization header...');
        response = await axios.put(`${API_BASE_URL}/auth/update-user`, updateData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'X-Auth-Token': token,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
        console.log('✅ PUT request successful!');
        console.log('Response status:', response.status);
        console.log('Response data:', response.data);
      } catch (err) {
        console.error('❌ PUT request failed:', err);
        console.error('Error response:', err.response);
        console.error('Error status:', err.response?.status);
        console.error('Error data:', err.response?.data);
        
        // If 405 (Method Not Allowed), try POST instead (some servers don't support PUT)
        if (err.response?.status === 405) {
          console.log('PUT not allowed (405), trying POST instead...');
          try {
            response = await axios.post(`${API_BASE_URL}/auth/update-user`, updateData, {
              headers: { 
                Authorization: `Bearer ${token}`,
                'X-Auth-Token': token,
                'X-HTTP-Method-Override': 'PUT', // Indicate this is actually a PUT operation
                'Content-Type': 'application/json'
              },
              withCredentials: true
            });
            console.log('✅ POST request (with PUT override) successful!');
            console.log('Response status:', response.status);
            console.log('Response data:', response.data);
          } catch (err2) {
            console.error('❌ POST request also failed:', err2);
            throw err2;
          }
        }
        // If 401, try with query parameter as last resort
        else if (err.response?.status === 401) {
          console.log('Authorization header failed, trying with query parameter...');
          try {
            response = await axios.put(`${API_BASE_URL}/auth/update-user?token=${token}`, updateData, {
              headers: { 
                'X-Auth-Token': token,
                'Content-Type': 'application/json'
              },
              withCredentials: true
            });
            console.log('✅ PUT request with query parameter successful!');
            console.log('Response status:', response.status);
            console.log('Response data:', response.data);
          } catch (err2) {
            // If PUT with query also fails with 405, try POST
            if (err2.response?.status === 405) {
              console.log('PUT with query also returned 405, trying POST...');
              try {
                response = await axios.post(`${API_BASE_URL}/auth/update-user?token=${token}`, updateData, {
                  headers: { 
                    'X-Auth-Token': token,
                    'X-HTTP-Method-Override': 'PUT',
                    'Content-Type': 'application/json'
                  },
                  withCredentials: true
                });
                console.log('✅ POST request with query parameter successful!');
              } catch (err3) {
                console.error('❌ POST request with query parameter also failed:', err3);
                throw err3;
              }
            } else {
              console.error('❌ PUT request with query parameter also failed:', err2);
              throw err2;
            }
          }
        } else {
          throw err;
        }
      }
      
      // Update formData with the response data from server (real-time update)
      if (response?.data?.user) {
        const updatedUserData = { ...response.data.user };
        
        console.log('=== SERVER RESPONSE RECEIVED ===');
        console.log('Full response:', response.data);
        console.log('User object from response:', updatedUserData);
        console.log('All fields in response:', Object.keys(updatedUserData));
        
        // Log each field value to verify all are present
        const allFields = ['firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 'heightFeet', 'heightInches', 'gender', 'address', 'city', 'state', 'zipCode', 'country', 'instagram'];
        allFields.forEach(field => {
          console.log(`${field}:`, updatedUserData[field], `(type: ${typeof updatedUserData[field]})`);
        });
        
        console.log('Date of birth in response:', updatedUserData.dateOfBirth);
        console.log('Date of birth type:', typeof updatedUserData.dateOfBirth);
        
        // Format dateOfBirth to YYYY-MM-DD format for date input
        // MySQL returns DATE fields as YYYY-MM-DD strings, use directly to avoid timezone issues
        let formattedDate = '';
        
        // Check if dateOfBirth exists and is not null/empty
        if (updatedUserData.dateOfBirth !== null && updatedUserData.dateOfBirth !== undefined && updatedUserData.dateOfBirth !== '') {
          const dateStr = String(updatedUserData.dateOfBirth).trim();
          console.log('Processing date string:', dateStr);
          
          // Check if it's already in YYYY-MM-DD format (most common case from MySQL)
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            // Already in correct format, use as is
            formattedDate = dateStr;
            console.log('Date of birth (already formatted):', dateStr);
          } else {
            // Try to extract YYYY-MM-DD from the string (handles timestamps, etc.)
            const dateMatch = dateStr.match(/(\d{4})-(\d{2})-(\d{2})/);
            if (dateMatch) {
              // Extract and use the date part directly
              formattedDate = dateMatch[0];
              console.log('Date of birth (extracted):', dateMatch[0], 'from:', dateStr);
            } else {
              // Last resort: parse manually to avoid timezone issues
              const dateParts = dateStr.split(/[-/T\s]/);
              if (dateParts.length >= 3) {
                const year = parseInt(dateParts[0], 10);
                const month = parseInt(dateParts[1], 10);
                const day = parseInt(dateParts[2], 10);
                if (!isNaN(year) && !isNaN(month) && !isNaN(day) && year > 1900 && year < 2100) {
                  formattedDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  console.log('Date of birth (parsed):', formattedDate, 'from:', dateStr);
                } else {
                  console.warn('Invalid date parts:', { year, month, day, original: dateStr });
                  formattedDate = '';
                }
              } else {
                console.warn('Could not parse date:', dateStr);
                formattedDate = '';
              }
            }
          }
        } else {
          console.log('Date of birth is null, undefined, or empty in response');
          formattedDate = '';
        }
        
        console.log('Final formatted date of birth:', formattedDate);
        
        // Update formData state with server response (real-time update)
        // Handle null values properly - convert to empty strings for form inputs
        // IMPORTANT: Ensure ALL fields from the response are included and properly formatted
        const newFormData = {
          firstName: String(updatedUserData.firstName ?? '').trim(),
          lastName: String(updatedUserData.lastName ?? '').trim(),
          email: String(updatedUserData.email ?? '').trim(),
          phone: updatedUserData.phone ? String(updatedUserData.phone).trim() : '',
          dateOfBirth: formattedDate, // Use the formatted date (empty string if null)
          heightFeet: updatedUserData.heightFeet ? String(updatedUserData.heightFeet) : '',
          heightInches: updatedUserData.heightInches ? String(updatedUserData.heightInches) : '',
          gender: updatedUserData.gender ? String(updatedUserData.gender).trim() : '',
          address: updatedUserData.address ? String(updatedUserData.address).trim() : '',
          city: updatedUserData.city ? String(updatedUserData.city).trim() : '',
          state: updatedUserData.state ? String(updatedUserData.state).trim() : '',
          zipCode: updatedUserData.zipCode ? String(updatedUserData.zipCode).trim() : '',
          country: updatedUserData.country ? String(updatedUserData.country).trim() : '',
          instagram: updatedUserData.instagram ? String(updatedUserData.instagram).trim() : ''
        };
        
        // Verify all expected fields are present and log each one
        const expectedFields = ['firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 'heightFeet', 'heightInches', 'gender', 'address', 'city', 'state', 'zipCode', 'country', 'instagram'];
        console.log('=== PREPARING FORM DATA UPDATE ===');
        expectedFields.forEach(field => {
          if (!(field in newFormData)) {
            console.warn(`⚠️ Missing field in newFormData: ${field} - adding default value`);
            newFormData[field] = '';
          }
          console.log(`✓ ${field}: "${newFormData[field]}"`);
        });
        
        console.log('=== ALL FIELDS VERIFIED ===');
        console.log('All fields in newFormData:', Object.keys(newFormData));
        console.log('Complete formData object:', JSON.stringify(newFormData, null, 2));
        console.log('Ready to update form state with all fields');
        
        // IMMEDIATELY update form data with server response - this ensures all fields show updated values right away
        console.log('✅ IMMEDIATELY updating formData with all fields from server response');
        console.log('New formData to set:', JSON.stringify(newFormData, null, 2));
        
        // CRITICAL: Update form state immediately with server response data
        // Use direct assignment (not functional) to ensure immediate update
        setFormData(newFormData);
        
        // Also update localStorage immediately
        const updatedUser = {
          ...JSON.parse(localStorage.getItem('user') || '{}'),
          ...updatedUserData,
          dateOfBirth: formattedDate || null // Use formatted date
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        console.log('✅ localStorage updated with new user data');
        console.log('✅ Form state updated with:', JSON.stringify(newFormData, null, 2));
        
        // Show success message
        setSuccess('Account updated successfully! Your changes are now visible in the form.');
        setError('');
        
        // CRITICAL: DO NOT refresh from server - we already have the correct data from the response
        // Any refresh would overwrite the updated values with potentially stale data
        console.log('✅ Update complete. Form should now show updated values.');
        console.log('✅ NO refresh will happen - using data directly from update response');
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess('');
        }, 5000);
      } else {
        // Fallback: response doesn't have user object - this shouldn't happen but handle it
        console.warn('⚠️ Response does not contain user object!');
        console.warn('Response data:', response.data);
        console.warn('Response status:', response.status);
        
        // Try to refresh user data from server
        try {
          console.log('Attempting to fetch updated user data...');
          const refreshResponse = await axios.get(`${API_BASE_URL}/auth/user`, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'X-Auth-Token': token
            },
            withCredentials: true
          });
          
          if (refreshResponse.data) {
            const refreshedData = { ...refreshResponse.data };
            console.log('Refreshed user data:', refreshedData);
            
            // Format date if present
            if (refreshedData.dateOfBirth) {
              const dateStr = String(refreshedData.dateOfBirth).trim();
              if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                refreshedData.dateOfBirth = dateStr;
              } else {
                const dateMatch = dateStr.match(/(\d{4})-(\d{2})-(\d{2})/);
                if (dateMatch) {
                  refreshedData.dateOfBirth = dateMatch[0];
                }
              }
            }
            
            // Update form with refreshed data
            const refreshedFormData = {
              firstName: refreshedData.firstName ?? '',
              lastName: refreshedData.lastName ?? '',
              email: refreshedData.email ?? '',
              phone: refreshedData.phone ?? '',
              dateOfBirth: refreshedData.dateOfBirth ?? '',
              heightFeet: refreshedData.heightFeet ?? '',
              heightInches: refreshedData.heightInches ?? '',
              gender: refreshedData.gender ?? '',
              address: refreshedData.address ?? '',
              city: refreshedData.city ?? '',
              state: refreshedData.state ?? '',
              zipCode: refreshedData.zipCode ?? '',
              country: refreshedData.country ?? '',
              instagram: refreshedData.instagram ?? ''
            };
            
            setFormData(refreshedFormData);
            console.log('Form updated with refreshed data');
          }
        } catch (refreshErr) {
          console.error('Failed to refresh user data:', refreshErr);
        }
        
        // Update localStorage with what we sent (best effort)
        const updatedUser = {
          ...JSON.parse(localStorage.getItem('user') || '{}'),
          ...updateData
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        setSuccess('Account updated successfully! Please refresh the page to see changes.');
        setError('');
        
        setTimeout(() => {
          setSuccess('');
        }, 5000);
      }
      
      // Don't navigate away - let user see the updated values in real-time
      // User can manually navigate if they want
    } catch (err) {
      console.error('❌❌❌ FAILED TO UPDATE USER ACCOUNT ❌❌❌');
      console.error('Error object:', err);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      console.error('Error config:', err.config);
      
      setSuccess(''); // Clear success message on error
      
      let errorMessage = 'Failed to update account. Please try again.';
      
      if (err.response) {
        // Server responded with error
        if (err.response.status === 401) {
          errorMessage = 'Session expired. Please log in again.';
        } else if (err.response.status === 400) {
          errorMessage = err.response.data?.message || 'Invalid data. Please check all fields and try again.';
        } else if (err.response.status === 500) {
          errorMessage = err.response.data?.message || 'Server error. Please try again later.';
        } else {
          errorMessage = err.response.data?.message || `Error ${err.response.status}: Failed to update account.`;
        }
      } else if (err.request) {
        // Request was made but no response received
        console.error('No response received from server');
        errorMessage = 'No response from server. Please check your internet connection and try again.';
      } else {
        // Error setting up the request
        console.error('Error setting up request:', err.message);
        errorMessage = `Request error: ${err.message}`;
      }
      
      setError(errorMessage);
      // Error is already displayed in the UI, no need for alert
    } finally {
      setLoading(false);
      console.log('Form submission completed. Loading set to false.');
    }
  };

  if (loadingUser) {
    return <div className="loading">Loading account information...</div>;
  }

  return (
    <div className="form-container">
      <h2>Edit My Account</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="firstName">First Name:</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name:</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone:</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="dateOfBirth">Date of Birth:</label>
          <input
            type="date"
            id="dateOfBirth"
            name="dateOfBirth"
            value={formData.dateOfBirth || ''}
            onChange={handleChange}
            onBlur={(e) => {
              // Ensure date is in correct format on blur
              const dateValue = e.target.value.trim();
              if (dateValue && !/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                console.warn('Invalid date format on blur:', dateValue);
              }
            }}
            min="1900-01-01"
            max={new Date().toISOString().split('T')[0]}
            style={{
              fontSize: '16px',
              padding: '10px',
              width: '100%',
              fontFamily: 'inherit',
              letterSpacing: '0.5px',
              textAlign: 'left'
            }}
            data-date-format="YYYY-MM-DD"
            placeholder="YYYY-MM-DD"
            autoComplete="bday"
          />
        </div>

        <div className="form-group">
          <label htmlFor="height">Height:</label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select
              id="heightFeet"
              name="heightFeet"
              value={formData.heightFeet}
              onChange={handleChange}
              style={{
                flex: '1',
                padding: '12px 16px',
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                fontSize: '16px',
                fontFamily: 'inherit'
              }}
            >
              <option value="">Feet</option>
              {Array.from({ length: 8 }, (_, i) => i + 1).map(feet => (
                <option key={feet} value={feet}>{feet} ft</option>
              ))}
            </select>
            <select
              id="heightInches"
              name="heightInches"
              value={formData.heightInches}
              onChange={handleChange}
              style={{
                flex: '1',
                padding: '12px 16px',
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                fontSize: '16px',
                fontFamily: 'inherit'
              }}
            >
              <option value="">Inches</option>
              {Array.from({ length: 12 }, (_, i) => i).map(inches => (
                <option key={inches} value={inches}>{inches} in</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="gender">Gender:</label>
          <select
            id="gender"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #e9ecef',
              borderRadius: '8px',
              fontSize: '16px',
              fontFamily: 'inherit',
              backgroundColor: '#fff'
            }}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="address">Address:</label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="city">City:</label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="state">State:</label>
          <input
            type="text"
            id="state"
            name="state"
            value={formData.state}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="zipCode">ZIP Code:</label>
          <input
            type="text"
            id="zipCode"
            name="zipCode"
            value={formData.zipCode}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="country">Country:</label>
          <input
            type="text"
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="instagram">Linked Instagram:</label>
          <input
            type="text"
            id="instagram"
            name="instagram"
            value={formData.instagram}
            onChange={handleChange}
            placeholder="Enter your Instagram username (e.g., @username)"
          />
        </div>

        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}
        
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button type="submit" className="btn" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditUserAccount;


